import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { fetchAuthSession } from 'aws-amplify/auth';
import { CognitoIdentityClient } from '@aws-sdk/client-cognito-identity';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';

// Identity Pool ID (deploy this first, then update this value)
const IDENTITY_POOL_ID = process.env.REACT_APP_IDENTITY_POOL_ID || 'us-east-1:REPLACE_WITH_IDENTITY_POOL_ID';
const AWS_REGION = process.env.REACT_APP_AWS_REGION || 'us-east-1';

/**
 * Parse S3 URI to bucket and key
 * @param {string} s3Uri - S3 URI like "s3://bucket-name/path/to/file.jpg"
 * @returns {object} - {bucket, key}
 */
export function parseS3Uri(s3Uri) {
  if (!s3Uri || !s3Uri.startsWith('s3://')) {
    return null;
  }

  const withoutProtocol = s3Uri.replace('s3://', '');
  const firstSlashIndex = withoutProtocol.indexOf('/');

  if (firstSlashIndex === -1) {
    return null;
  }

  const bucket = withoutProtocol.substring(0, firstSlashIndex);
  const key = withoutProtocol.substring(firstSlashIndex + 1);

  return { bucket, key };
}

/**
 * Generate pre-signed URL for an S3 object
 * @param {string} s3Uri - S3 URI like "s3://bucket-name/path/to/file.jpg"
 * @param {number} expiresIn - URL expiration time in seconds (default: 3600 = 1 hour)
 * @returns {Promise<string>} - Pre-signed URL
 */
export async function getSignedS3Url(s3Uri, expiresIn = 3600) {
  try {
    // Parse S3 URI
    const parsed = parseS3Uri(s3Uri);
    if (!parsed) {
      console.error('Invalid S3 URI:', s3Uri);
      return null;
    }

    const { bucket, key } = parsed;

    // Get temporary AWS credentials from Cognito Identity Pool
    const credentials = await getAwsCredentials();

    // Create S3 client with temporary credentials
    const s3Client = new S3Client({
      region: AWS_REGION,
      credentials, // AWS SDK accepts credential provider directly
    });

    // Create GetObject command
    const command = new GetObjectCommand({
      Bucket: bucket,
      Key: key,
    });

    // Generate pre-signed URL
    const signedUrl = await getSignedUrl(s3Client, command, { expiresIn });

    return signedUrl;
  } catch (error) {
    console.error('Error generating pre-signed URL for:', s3Uri, error);
    return null;
  }
}

/**
 * OPTIMIZADO: Obtiene las credenciales UNA VEZ y las devuelve.
 */
async function getAwsCredentials() {
  try {
    const session = await fetchAuthSession();
    if (!session.tokens || !session.tokens.idToken) {
      throw new Error('No ID token available.');
    }

    const idToken = session.tokens.idToken.toString();

    // Retornamos el proveedor de credenciales (el SDK se encarga del refresh si es necesario)
    return fromCognitoIdentityPool({
      client: new CognitoIdentityClient({ region: AWS_REGION }),
      identityPoolId: IDENTITY_POOL_ID,
      logins: {
        [`cognito-idp.${AWS_REGION}.amazonaws.com/${process.env.REACT_APP_AUTH_USER_POOL_ID || 'us-east-1_9EXSggCRi'}`]: idToken,
      },
    });
  } catch (error) {
    console.error('Error getting AWS credentials:', error);
    throw error;
  }
}

/**
 * OPTIMIZADO: Ahora recibe el cliente S3 ya configurado para no recrearlo.
 */
async function getSignedS3UrlWithClient(s3Client, s3Uri, expiresIn = 3600) {
  const parsed = parseS3Uri(s3Uri);
  if (!parsed) return null;

  try {
    const command = new GetObjectCommand({
      Bucket: parsed.bucket,
      Key: parsed.key,
    });
    return await getSignedUrl(s3Client, command, { expiresIn });
  } catch (error) {
    console.error('Error signing:', s3Uri, error);
    return null;
  }
}

/**
 * VERSIÓN CORREGIDA: Firma múltiples URLs usando una única sesión de credenciales.
 */
export async function signMultipleS3Urls(s3Uris, expiresIn = 3600) {
  if (!Array.isArray(s3Uris) || s3Uris.length === 0) return {};

  try {
    // 1. Obtener credenciales UNA SOLA VEZ
    const credentials = await getAwsCredentials();

    // 2. Crear UN SOLO cliente S3 para todas las operaciones
    const s3Client = new S3Client({
      region: AWS_REGION,
      credentials,
    });

    // 3. Firmar localmente (esto ya no hace peticiones a Cognito)
    const signedUrlPromises = s3Uris.map(async (uri) => {
      const signedUrl = await getSignedS3UrlWithClient(s3Client, uri, expiresIn);
      return { uri, signedUrl };
    });

    const results = await Promise.all(signedUrlPromises);

    const urlMap = {};
    results.forEach(({ uri, signedUrl }) => {
      if (signedUrl) urlMap[uri] = signedUrl;
    });

    return urlMap;
  } catch (error) {
    console.error('Error signing multiple URLs:', error);
    return {};
  }
}

/**
 * Check if user has AWS credentials (is authenticated with Identity Pool)
 * @returns {Promise<boolean>}
 */
export async function hasAwsCredentials() {
  try {
    const session = await fetchAuthSession({ forceRefresh: false });
    return !!session.credentials;
  } catch (error) {
    return false;
  }
}